import {
	Box,
	Chip,
	Paper,
	Table,
	TableBody,
	TableContainer,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	alpha,
	styled,
	useTheme,
} from "@mui/material"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import * as hooks from "lib/hooks"
import { FileData } from "lib/util"
import * as util from "lib/util"
import { ElevatedPaperStyle } from "lib/styles/shared"
import { GadgeStyleLable, GetSharedTableStyles, TableContainerStyles } from "lib/styles/table"
import { Notice } from "./Notice"

/* --- styled helpers ----------------------------------------------------- */

const styledColumnChip = styled(Chip)(({ theme }) => ({
	height: 30,
	borderRadius: theme.shape.borderRadius,
	opacity: 0.6,
	"& .MuiChip-label": {
		padding: `0 ${theme.spacing(1)}`,
		fontSize: "1em",
	},
}))

export const StyledColumnChip = styledColumnChip;

/* --- component ---------------------------------------------------------- */

export function FilePreview({
	showHighlightedCols, // not implemented here, but kept for API parity
	showRemappedHeaders,
	hidePointers,
	hideTitle,
}: {
	showHighlightedCols?: boolean
	showRemappedHeaders?: boolean
	hidePointers?: boolean
	hideTitle?: boolean
	replaceField?: Partial<FileData>
}) {
	const { formState } = hooks.UseFormContext()
	const theme = useTheme()

	const previewData = formState.Map((f) => f.parsed).UnwrapOr([])
	const fileInfo = formState.AndThen((f) => f.file)

	const originalKeys = formState
		.Map((f) => f.parsed.First().UnwrapOr({}))
		.Map(Object.keys)
		.UnwrapOr([])

	/* ---------------- empty-state --------------------------------------- */
	if (previewData.length === 0) {
		return (
			<Paper sx={ElevatedPaperStyle}>
				<Box sx={{ textAlign: "center", py: 6 }}>
					<InsertDriveFileOutlinedIcon
						sx={{
							fontSize: 60,
							color: alpha(theme.palette.text.secondary, 0.3),
							mb: 2,
						}}
					/>
					<Typography variant="h6" color="text.secondary">
						No data available
					</Typography>
				</Box>
			</Paper>
		)
	}

	/* ----------------- table render ------------------------------------ */
	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
			{!hideTitle && (
				<Box
					sx={GadgeStyleLable}
				>
					{fileInfo.Map((f) => f.name).UnwrapOr("")}
				</Box>
			)}


			<TableContainer sx={{ ...TableContainerStyles(), maxWidth: "100%" }}>
			<Table
				size="small"
				stickyHeader
				sx={[
					GetSharedTableStyles().root,
					{
						tableLayout: "fixed",
						width: "100%",
						"& .MuiTableCell-root": {
							wordBreak: "break-word",
							overflowWrap: "break-word",
						},
					},
				]}
			>
				<TableHead>
					<TableRow>
						{originalKeys.Map((key: string) => (
							<TableCell key={key}>{key}</TableCell>
						))}
					</TableRow>
				</TableHead>

				<TableBody>
					{previewData.Map((row: Record<string, any>, idx: number) => (
						<TableRow key={idx}>
							{originalKeys.Map((key: string) => (
								<TableCell key={key}>
									{row[key] ?? <b>{util.EmptyCell}</b>}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
			</TableContainer>

			{/* footer note */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mt: 2,
					px: 1,
					opacity: 0.7,
				}}
			>
				<Notice message={"Note: only showing top " + previewData.length + " rows"}>
				</Notice>


				<StyledColumnChip
					label={`${originalKeys.length} columns`}

				/>
			</Box>
		</Box>
	)
}
